import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { fieldsByGroup, SYNERGY_GROUPS, LEVELS } from "../../lib/seasonStats";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");
const inputCls = "w-full bg-[#0f0f0f] border border-white/10 px-2.5 py-1.5 text-sm text-white focus:outline-none focus:border-[var(--org-accent)] font-mono";

// Grouped manual-entry form. Empty boxes stay empty (never 0). Synergy groups collapse for Prep / HS players.
export function SeasonStatsForm({ playerId, lib, record, isNew, onSaved, onDeleted }) {
  const [season, setSeason] = useState(record?.season || "");
  const [level, setLevel] = useState(record?.level || "");
  const [teamLeague, setTeamLeague] = useState(record?.team_league || "");
  const [vals, setVals] = useState({});
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState({});
  useEffect(() => {
    setSeason(record?.season || ""); setLevel(record?.level || ""); setTeamLeague(record?.team_league || "");
    setVals(Object.fromEntries(Object.entries(record?.stats || {}).map(([k, v]) => [k, String(v)])));
    const prep = !record?.level || /prep|hs|high/i.test(record.level);
    setOpen(Object.fromEntries(fieldsByGroup(lib).map(({ group, fields }) => [group, !(SYNERGY_GROUPS.has(group) && prep && !fields.some(f => record?.stats?.[f.id] != null))])));
  }, [record, lib]);
  const groups = useMemo(() => fieldsByGroup(lib), [lib]);
  const filled = (fields) => fields.filter(f => (vals[f.id] ?? "") !== "").length;

  const save = async () => {
    if (!season.trim()) { toast.error("Season label is required (e.g. 2026-27)"); return; }
    setSaving(true);
    try {
      const stats = {}; const cleared = [];
      for (const [k, v] of Object.entries(vals)) { if (v === "" || v == null) { if (record?.stats?.[k] != null) cleared.push(k); } else stats[k] = v; }
      const doc = await api.saveSeasonStats(playerId, season.trim(), { season: season.trim(), level, team_league: teamLeague, stats, cleared, source: record?.source && record.source !== "Manual" ? record.source : "Manual" });
      toast.success(`Season Stats saved · ${doc.season}`);
      onSaved?.(doc);
    } catch (e) { toast.error("Save failed", { description: e?.response?.data?.detail || e.message }); }
    finally { setSaving(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete Season Stats for ${record.season}? This cannot be undone.`)) return;
    try { await api.deleteSeasonStats(playerId, record.season); toast.success("Deleted"); onDeleted?.(record.season); }
    catch (e) { toast.error("Delete failed", { description: e?.response?.data?.detail || e.message }); }
  };

  return (
    <div className="border border-[var(--org-accent)]/40 bg-[#141414] p-5 space-y-5" data-testid="season-stats-form">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <label className="block">
          <div className="text-[9px] uppercase tracking-[0.3em] text-[#a0a0a0] mb-1">Season</div>
          <input value={season} onChange={e => setSeason(e.target.value)} disabled={!isNew} placeholder="2026-27" className={`${inputCls} disabled:opacity-60`} data-testid="ss-season-input" />
        </label>
        <label className="block">
          <div className="text-[9px] uppercase tracking-[0.3em] text-[#a0a0a0] mb-1">Level</div>
          <select value={level} onChange={e => setLevel(e.target.value)} className={inputCls} data-testid="ss-level-select">
            <option value="">—</option>{LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
        </label>
        <label className="block">
          <div className="text-[9px] uppercase tracking-[0.3em] text-[#a0a0a0] mb-1">Team / League</div>
          <input value={teamLeague} onChange={e => setTeamLeague(e.target.value)} placeholder="e.g. Williams College · NESCAC" className={inputCls} data-testid="ss-team-input" />
        </label>
      </div>
      {groups.map(({ group, fields }) => (
        <div key={group} className="border border-white/10" data-testid={`ss-group-${slug(group)}`}>
          <button type="button" onClick={() => setOpen(o => ({ ...o, [group]: !o[group] }))} className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-white/[0.03]" data-testid={`ss-group-toggle-${slug(group)}`}>
            {open[group] ? <ChevronDown className="w-3.5 h-3.5 text-[var(--org-accent)]" /> : <ChevronRight className="w-3.5 h-3.5 text-[#757575]" />}
            <span className="text-[10px] uppercase tracking-[0.35em] font-bold text-white">{group}</span>
            <span className="ml-auto text-[10px] font-mono text-[#757575]">{filled(fields)} / {fields.length}</span>
          </button>
          {open[group] && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-3 px-4 pb-4">
              {fields.map(f => (
                <label key={f.id} className="block" title={f.note || f.name}>
                  <div className="text-[10px] text-[#a0a0a0] leading-tight mb-1 truncate">{f.name}</div>
                  <input type="number" step="any" inputMode="decimal" value={vals[f.id] ?? ""} onChange={e => setVals(v => ({ ...v, [f.id]: e.target.value }))}
                    placeholder="" className={inputCls} data-testid={`ss-field-${f.id}`} />
                </label>
              ))}
            </div>
          )}
        </div>
      ))}
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={save} disabled={saving} className="flex items-center gap-2 px-5 py-2.5 bg-[var(--org-accent)] text-[#121212] text-[10px] uppercase tracking-[0.3em] font-bold hover:bg-[var(--org-accent-hover)] disabled:opacity-60" data-testid="ss-save-btn">
          <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save Season Stats"}
        </button>
        {!isNew && <button onClick={remove} className="flex items-center gap-2 px-3 py-2.5 border border-red-500/30 text-red-300 text-[10px] uppercase tracking-[0.3em] font-bold hover:bg-red-500/10" data-testid="ss-delete-btn"><Trash2 className="w-3.5 h-3.5" /> Delete</button>}
        <div className="text-[10px] text-[#757575]">Enter season totals, not per-game. Leave a box blank if you don't have it — blanks stay blank.</div>
      </div>
    </div>
  );
}
